import React from 'react'
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer'
import type { DrawingElement } from 'features/Procedure/state/procedure.type'
import type {
  Provider,
  PatientWithEncounters,
  PatientEncounter,
  ProcedureItem,
} from 'features/Procedure/state/procedure-api.type'
import { UtilService } from 'common/services/utilService'
import dayjs from 'dayjs'

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  logo: {
    width: 30,
    height: 30,
  },
  dateText: {
    fontSize: 10,
    color: '#000000',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#c8c8c8',
    marginVertical: 8,
  },
  patientSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    marginTop: 8,
  },
  patientNameContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 4,
  },
  avatarContainer: {
    width: 32,
    height: 32,
    borderRadius: 100,
    backgroundColor: '#c8c8c8',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 9,
  },
  avatarText: {
    fontSize: 14,
    color: 'black',
    fontFamily: 'Helvetica-Bold',
  },
  patientName: {
    fontSize: 12,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
  },
  contentSection: {
    marginTop: 10,
  },
  procedureTitleContainer: {
    fontSize: 12,
    marginBottom: 8,
    color: '#000000',
  },
  procedureTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
  },
  procedureTitleText: {
    fontSize: 12,
    fontFamily: 'Helvetica',
    fontStyle: 'italic',
    color: '#000000',
  },
  procedureImage: {
    width: '100%',
    marginBottom: 10,
  },
  productsSection: {
    marginTop: 10,
  },
  productsTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
  },
  productItem: {
    fontSize: 10,
    fontFamily: 'Helvetica',
    marginBottom: 6,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 15,
    right: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 6,
    borderTopWidth: 0.1,
    borderTopColor: '#c8c8c8',
  },
  footerLogo: {
    width: 8,
    height: 8,
  },
  footerText: {
    fontSize: 8,
    color: '#646464',
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  footerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  footerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
})

type Props = {
  images: { procedureId: number; imageData: string; elements: DrawingElement[] }[]
  providerData: Provider | null
  patientData: PatientWithEncounters | null
  encounterData: PatientEncounter | null
  proceduresMap: Map<number, ProcedureItem>
  logoDataURL: string
  timeZone: string
}

const Avatar: React.FC<{ initials: string }> = ({ initials }) => (
  <View style={styles.avatarContainer}>
    <Text style={styles.avatarText}>{initials || '?'}</Text>
  </View>
)

export const ProcedurePDFDocument = ({
  images,
  patientData,
  encounterData,
  proceduresMap,
  logoDataURL,
  timeZone,
}: Props) => {
  const formatDate = (date: string, options: Intl.DateTimeFormatOptions = {}): string => {
    if (!date) return 'N/A'
    const dateObj = dayjs(date).toDate()

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone,
      ...options,
    }).format(dateObj)
  }
  return (
    <Document>
      {images.map((item, index) => {
        const procedure = proceduresMap.get(item.procedureId)

        const patient = patientData?.patient

        const firstName = patient?.first_name || ''
        const lastName = patient?.last_name || ''
        const initials = UtilService.getInitials(firstName, lastName)
        const patientName = [firstName, lastName].filter(Boolean).join(' ') || 'N/A'

        const dateOfService = encounterData ? formatDate(encounterData.encounter_date) : 'N/A'

        const pageHeight = 297
        const imageHeight = pageHeight * 1

        return (
          <Page key={index} size='A4' style={styles.page}>
            <View style={styles.header}>
              {logoDataURL && <Image src={logoDataURL} style={styles.logo} />}
              <Text style={styles.dateText}>{dateOfService}</Text>
            </View>

            <View style={styles.divider} />

            {patientData && (
              <View style={styles.patientSection}>
                <Avatar initials={initials || '?'} />
                <View style={styles.patientNameContainer}>
                  <Text style={styles.patientName}>{patientName}</Text>
                  <Text>
                    {`(${patient?.sex ?? 'Unknown gender'} | ${
                      patient?.age ?? 'Unknown age'
                    } • ${formatDate(patient?.dob, {
                      hour: undefined,
                      minute: undefined,
                    })})`}
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.divider} />
            <View style={styles.contentSection}>
              {procedure && procedure.code_text && (
                <Text style={styles.procedureTitleContainer}>
                  <Text style={styles.procedureTitle}>Procedure: </Text>
                  <Text style={styles.procedureTitleText}>{procedure.code_text}</Text>
                </Text>
              )}
              {item.imageData && (
                <Image
                  src={item.imageData}
                  style={[styles.procedureImage, { height: imageHeight }]}
                />
              )}

              {/* {procedure && procedure.product_labels && procedure.product_labels.length > 0 && (
                <View style={styles.productsSection}>
                  <Text style={styles.productsTitle}>Products:</Text>
                  {procedure.product_labels.map((product, idx) => (
                    <Text key={idx} style={styles.productItem}>
                      • {product}
                    </Text>
                  ))}
                </View>
              )} */}
            </View>

            <View style={styles.footer} fixed>
              <View style={styles.footerLeft}>
                {logoDataURL && <Image src={logoDataURL} style={styles.footerLogo} />}
                <Text style={[styles.footerText, { marginLeft: logoDataURL ? 4 : 0 }]}>
                  Powered by Residen
                </Text>
              </View>
              <View style={styles.footerCenter}>
                <Text style={styles.footerText}>www.residen.com</Text>
              </View>
              <View style={styles.footerRight}>
                <Text style={styles.footerText}>
                  Page {index + 1} of {images.length}
                </Text>
              </View>
            </View>
          </Page>
        )
      })}
    </Document>
  )
}
